import { useCallback, useEffect, useState } from 'react';

/** How far a zoomed image is blown up past its fitted size. */
const ZOOM = 2.5;

export type ImageZoom = {
  zoomed: boolean;
  /** The factor to draw at, so a caller need not know `ZOOM`. */
  scale: number;
  toggle: () => void;
  reset: () => void;
};

/**
 * One step of zoom on a lightbox image, and the panning that follows from it.
 *
 * Two states and not a continuous gesture. A pinch-zoom rig that owns the
 * transform has to own panning, momentum, bounds and the double-tap that
 * undoes it, and it fights the browser's own pinch the whole way; one honest
 * step covers what people actually want from a gallery, which is to read a
 * number plate or a signature.
 *
 * The caller draws the zoomed image *larger in layout* rather than
 * transformed, inside a scrollable box — so panning is the browser's own
 * scrolling, with its own momentum and its own bounds, and a pinch on top of
 * it still works because nothing here has claimed the gesture.
 *
 * Resets whenever the image changes, which is why `reset` exists: paging to
 * the next photograph while still zoomed into the corner of the last one lands
 * a visitor somewhere they did not ask to be.
 */
export function useImageZoom(resetKey: string | number): ImageZoom {
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    setZoomed(false);
  }, [resetKey]);

  const toggle = useCallback(() => setZoomed((on) => !on), []);
  const reset = useCallback(() => setZoomed(false), []);

  return { zoomed, scale: ZOOM, toggle, reset };
}
